import React from 'react';
import { Tag, Tooltip, Typography } from 'antd';
import PropTypes from 'prop-types';

import { CommentOutlined, LockOutlined } from '@ant-design/icons';

import TradeOutcomeBar from './TradeOutcomeBar';

const { Text } = Typography;

// Format currency values
const formatCurrency = (value) => {
    if (value === null || value === undefined) return '-';
    const prefix = value >= 0 ? '+$' : '-$';
    return `${prefix}${Math.abs(value).toFixed(2)}`;
};

// Format percentage values
const formatPercent = (value) => {
    if (value === null || value === undefined) return '-';
    return `${value.toFixed(1)}%`;
};

// Format ratio values
const formatRatio = (value) => {
    if (value === null || value === undefined) return '-';
    return `${value.toFixed(2)}:1`;
};

export default function SetOverviewRow({ set, setType, onClick, hasComment, onCommentClick }) {
    const pnlClass = set.totalPnL >= 0 ? 'positive' : 'negative';

    const displayName = setType === 'noStrategy' ? `No Strategy #${set.setNumber}` : set.setName;

    return (
        <div
            className={`set-overview-row ${set.isFull ? 'full' : ''}`}
            onClick={onClick}
            role="button"
            tabIndex={0}
            onKeyPress={(e) => e.key === 'Enter' && onClick()}
        >
            {/* Set Name with Full Indicator and Comment Indicator */}
            <div className="cell set-name">
                <Text strong className="clickable-text">
                    {displayName}
                </Text>
                <Tooltip
                    title={
                        hasComment
                            ? 'This set has a comment - click to view'
                            : 'Click to add or view comment'
                    }
                >
                    <CommentOutlined
                        className="comment-indicator"
                        style={{
                            marginLet: 8,
                            color: hasComment ? '#1890ff' : '#ccc',
                            cursor: 'pointer'
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            onCommentClick?.();
                        }}
                    />
                </Tooltip>
                {set.isFull && (
                    <Tooltip title="This set has reached the 20-trade limit">
                        <Tag icon={<LockOutlined />} color="orange" className="full-tag">
                            Full
                        </Tag>
                    </Tooltip>
                )}
            </div>

            {/* Total Trades */}
            <div className="cell trades">
                <Text>{set.totalTrades}/20</Text>
            </div>

            {/* Discipline % */}
            <div className="cell discipline">
                <Text
                    type={
                        set.disciplinePercent !== null && set.disciplinePercent >= 80
                            ? 'success'
                            : undefined
                    }
                >
                    {formatPercent(set.disciplinePercent)}
                </Text>
            </div>

            {/* Win Rate */}
            <div className="cell win-rate">
                <Text type={set.winRate >= 50 ? 'success' : 'danger'}>
                    {formatPercent(set.winRate)}
                </Text>
            </div>

            {/* Average Win */}
            <div className="cell avg-win">
                <Text type="success">{formatCurrency(set.averageWin)}</Text>
            </div>

            {/* Average Loss */}
            <div className="cell avg-loss">
                <Text type="danger">{formatCurrency(-Math.abs(set.averageLoss || 0))}</Text>
            </div>

            {/* Win:Loss Ratio */}
            <div className="cell ratio">
                <Text type={set.winLossRatio >= 1 ? 'success' : 'warning'}>
                    {formatRatio(set.winLossRatio)}
                </Text>
            </div>

            {/* Average Trades Per Day */}
            <div className="cell avg-day">
                <Text>{set.avgTradesPerDay?.toFixed(1) || '-'}</Text>
            </div>

            {/* Total PnL */}
            <div className={`cell pnl ${pnlClass}`}>
                <Text strong type={set.totalPnL >= 0 ? 'success' : 'danger'}>
                    {formatCurrency(set.totalPnL)}
                </Text>
            </div>

            {/* Trade Outcomes Visual Bar */}
            <div className="cell outcomes">
                <TradeOutcomeBar outcomes={set.tradeOutcomes} maxTrades={20} />
            </div>
        </div>
    );
}

SetOverviewRow.propTypes = {
    set: PropTypes.shape({
        setName: PropTypes.string.isRequired,
        setNumber: PropTypes.number.isRequired,
        totalTrades: PropTypes.number.isRequired,
        totalPnL: PropTypes.number.isRequired,
        winRate: PropTypes.number,
        averageWin: PropTypes.number,
        averageLoss: PropTypes.number,
        winLossRatio: PropTypes.number,
        disciplinePercent: PropTypes.number,
        avgTradesPerDay: PropTypes.number,
        tradeOutcomes: PropTypes.arrayOf(
            PropTypes.shape({
                tradeId: PropTypes.string,
                pnl: PropTypes.number,
                isWin: PropTypes.bool
            })
        ),
        isFull: PropTypes.bool
    }).isRequired,
    setType: PropTypes.string.isRequired,
    onClick: PropTypes.func.isRequired,
    hasComment: PropTypes.bool,
    onCommentClick: PropTypes.func
};

SetOverviewRow.defaultProps = {
    hasComment: false,
    onCommentClick: null
};
